import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useApi } from "@/core/api";
import { RUN_PAGE_SIZE, RUN_POLL_INTERVAL_MS } from "./constants";
import type { ProfitabilityRun, ReportDates, RunHistoryPage } from "./types";
import {
  enrichProfitabilityPeriod,
  enrichProfitabilityRun,
  isActiveRun,
  reportPath,
} from "./utils";

export const reportKey = (businessId: string | null) =>
  ["profitability", businessId] as const;

const enrichReportHistory = (
  history: InfiniteData<RunHistoryPage, number>,
) => ({
  ...history,
  pages: history.pages.map((page) => ({
    ...page,
    items: page.items.map(enrichProfitabilityPeriod),
  })),
});

export const useReportHistory = (businessId: string | null) => {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: [...reportKey(businessId), "history"],
    initialPageParam: 1,
    select: enrichReportHistory,
    queryFn: ({ pageParam, signal }) =>
      api
        .get<RunHistoryPage>(
          `/businesses/${businessId}/profitability_report_periods`,
          {
            params: { page: pageParam, page_size: RUN_PAGE_SIZE },
            signal,
          },
        )
        .then((res) => res.data),
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : null,
    enabled: !!businessId,
    // Every loaded page refetches together, so a run that finishes while the
    // list is open lands on whichever page it is already sitting on.
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) =>
        page.items.some((period) => isActiveRun(period.latest_attempt)),
      )
        ? RUN_POLL_INTERVAL_MS
        : false,
    retry: false,
  });
};

export const useReportRun = (businessId: string | null, runId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [...reportKey(businessId), "run", runId],
    select: enrichProfitabilityRun,
    queryFn: ({ signal }) =>
      api
        .get<{ run: ProfitabilityRun }>(reportPath(businessId, runId), {
          signal,
        })
        .then((res) => res.data.run),
    enabled: !!businessId && !!runId,
    refetchInterval: (query) =>
      query.state.data && isActiveRun(query.state.data)
        ? RUN_POLL_INTERVAL_MS
        : false,
    retry: false,
  });
};

export const useCreateReport = (businessId: string | null) => {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dates: ReportDates) =>
      api
        .post<{ run: ProfitabilityRun }>(reportPath(businessId), dates)
        .then((res) => res.data.run),
    onSuccess: (run) => {
      client.setQueryData([...reportKey(businessId), "run", run.id], run);
      void client.invalidateQueries({
        queryKey: [...reportKey(businessId), "history"],
      });
    },
  });
};

/**
 * A completed report usually downloads on the first request. When the stored
 * file is missing the API rebuilds it and hands back a polling token, so this
 * keeps asking until the file exists, the caller navigates away, or the wait
 * runs past its bound.
 */
export const downloadReport = async (
  api: AxiosInstance,
  businessId: string,
  runId: string,
  signal: AbortSignal,
): Promise<Blob> => {
  let token: string | undefined;
  // A bounded wait also handles an export worker that is no longer available.
  for (let attempt = 0; attempt < 150; attempt++) {
    signal.throwIfAborted();
    const response = await api.get<Blob>(
      `${reportPath(businessId, runId)}/workbook/download`,
      { responseType: "blob", params: token ? { token } : undefined, signal },
    );
    if (response.status === 200) return response.data;
    if (response.status !== 202)
      throw new Error("The download failed. Try again.");
    const result = JSON.parse(await response.data.text()) as {
      status: string;
      token?: string;
    };
    if (result.status !== "processing" || !result.token)
      throw new Error("The export response was invalid. Try again.");
    token = result.token;
    await new Promise<void>((resolve, reject) => {
      const abort = () => {
        clearTimeout(timer);
        reject(new DOMException("Download cancelled", "AbortError"));
      };
      const timer = setTimeout(() => {
        signal.removeEventListener("abort", abort);
        resolve();
      }, RUN_POLL_INTERVAL_MS);
      signal.addEventListener("abort", abort, { once: true });
      if (signal.aborted) abort();
    });
  }
  throw new Error("The export is taking too long. Try the download again.");
};
